const MOBILE = window.matchMedia('(max-width: 1023px)');
const COLLAPSED_KEY = 'mellow.sidebar.collapsed';

/**
 * <mellow-sidebar>: a regular column on large screens, an off-canvas drawer on small ones.
 * While the drawer is open the rest of the page is inert, Escape and the backdrop close it.
 */
export class MellowSidebar extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                event.stopPropagation();
                this.close();
            }
        });

        // Following a link inside the drawer (e.g. with wire:navigate) closes it.
        this.addEventListener('click', (event) => {
            if (this.isOpen && event.target.closest('a[href]')) this.close(false);
        });

        MOBILE.addEventListener('change', () => this.close(false));

        if (this.hasAttribute('data-collapsible')) {
            let collapsed = false;
            try {
                collapsed = localStorage.getItem(COLLAPSED_KEY) === 'true';
            } catch {}
            this.toggleAttribute('data-collapsed', collapsed);
        }

        this.revealCurrent();
        this.sync();
    }

    /** Scroll a sticky sidebar so the current page's link is visible. */
    revealCurrent() {
        const current = this.querySelector('[aria-current="page"]');
        if (!current || this.scrollHeight <= this.clientHeight) return;

        const top = current.getBoundingClientRect().top - this.getBoundingClientRect().top + this.scrollTop;
        if (top < this.scrollTop || top > this.scrollTop + this.clientHeight - current.offsetHeight) {
            this.scrollTop = top - this.clientHeight / 3;
        }
    }

    get isOpen() {
        return this.hasAttribute('data-open');
    }

    toggles() {
        return document.querySelectorAll(`[data-mellow-sidebar-toggle="${this.id}"]`);
    }

    open() {
        if (!MOBILE.matches || this.isOpen) return;
        this._returnFocus = document.activeElement;
        this.setAttribute('data-open', '');
        this.setInert(true);

        // Focus the first link or the close button inside the drawer.
        (this.querySelector('[aria-current="page"]') ?? this.querySelector('a[href], button'))?.focus();
        this.sync();
    }

    close(restoreFocus = true) {
        if (!this.isOpen) return;
        this.removeAttribute('data-open');
        this.setInert(false);
        if (restoreFocus) this._returnFocus?.focus?.();
        this.sync();
    }

    toggle() {
        this.isOpen ? this.close() : this.open();
    }

    collapse(collapsed = !this.hasAttribute('data-collapsed')) {
        this.toggleAttribute('data-collapsed', collapsed);
        try {
            localStorage.setItem(COLLAPSED_KEY, String(collapsed));
        } catch {}
        this.sync();
    }

    /** Everything but the drawer (and overlays in the top layer) becomes inert while open. */
    setInert(inert) {
        const container = this.parentElement;
        [...container.children].forEach((sibling) => {
            if (sibling === this || sibling.matches('script, template, mellow-toast-region')) return;
            if (inert) {
                if (!sibling.inert) {
                    sibling.inert = true;
                    sibling.dataset.mellowInert = '';
                }
            } else if ('mellowInert' in sibling.dataset) {
                sibling.inert = false;
                delete sibling.dataset.mellowInert;
            }
        });
        document.documentElement.toggleAttribute('data-mellow-scroll-locked', inert);
    }

    /** Reflect state on the toggle buttons (also after Livewire morphs). */
    sync() {
        this.toggles().forEach((toggle) => toggle.setAttribute('aria-expanded', String(this.isOpen)));
        document.querySelectorAll(`[data-mellow-sidebar-collapse="${this.id}"]`).forEach((button) => {
            button.setAttribute('aria-expanded', String(!this.hasAttribute('data-collapsed')));
        });
    }
}

export function registerNavigationBehaviors() {
    document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-mellow-sidebar-toggle]');
        if (toggle) {
            document.getElementById(toggle.dataset.mellowSidebarToggle)?.toggle?.();
            return;
        }

        const collapse = event.target.closest('[data-mellow-sidebar-collapse]');
        if (collapse) document.getElementById(collapse.dataset.mellowSidebarCollapse)?.collapse?.();

        // Clicking the drawer backdrop (the ::before of the open sidebar sits behind it).
        const open = document.querySelector('mellow-sidebar[data-open]');
        if (open && !open.contains(event.target) && !event.target.closest('[data-mellow-sidebar-toggle]')) open.close();
    });
}

/**
 * <mellow-tablist>: WAI-ARIA tabs with automatic activation. `.value` is the selected tab
 * name and changes fire `input`, so `wire:model` on <mellow:tabs> works.
 */
export class MellowTablist extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.addEventListener('click', (event) => {
            const tab = event.target.closest('[role="tab"]');
            if (tab && !tab.disabled) this.select(tab, true);
        });

        this.addEventListener('keydown', (event) => {
            const tabs = this.tabs.filter((tab) => !tab.disabled);
            const index = tabs.indexOf(event.target.closest('[role="tab"]'));
            if (index === -1) return;

            const rtl = getComputedStyle(this).direction === 'rtl';
            const vertical = this.getAttribute('aria-orientation') === 'vertical';
            const next = vertical ? 'ArrowDown' : rtl ? 'ArrowLeft' : 'ArrowRight';
            const previous = vertical ? 'ArrowUp' : rtl ? 'ArrowRight' : 'ArrowLeft';

            const target = {
                [next]: tabs[(index + 1) % tabs.length],
                [previous]: tabs[(index - 1 + tabs.length) % tabs.length],
                Home: tabs[0],
                End: tabs.at(-1),
            }[event.key];

            if (target) {
                event.preventDefault();
                target.focus();
                this.select(target, true);
            }
        });

        // Tabs used as a plain switcher have no panels: don't point at missing elements.
        const unlink = () => this.tabs.forEach((tab) => {
            const panel = tab.getAttribute('aria-controls');
            if (panel && !document.getElementById(panel)) tab.removeAttribute('aria-controls');
        });
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', unlink, { once: true });
        else unlink();
    }

    get tabs() {
        return [...this.querySelectorAll('[role="tab"]')];
    }

    get value() {
        return this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.name ?? null;
    }

    set value(name) {
        const tab = this.tabs.find((t) => t.dataset.name === String(name));
        if (tab) this.select(tab, false);
    }

    select(tab, emit) {
        const changed = tab.getAttribute('aria-selected') !== 'true';

        this.tabs.forEach((t) => {
            const selected = t === tab;
            t.setAttribute('aria-selected', String(selected));
            t.tabIndex = selected ? 0 : -1;

            const panel = document.getElementById(t.getAttribute('aria-controls'));
            if (panel) panel.hidden = !selected;
        });

        if (emit && changed) {
            this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: tab.dataset.name }));
            this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: tab.dataset.name }));
        }
    }
}
